import {
  CurrencyValue,
  Chart,
  formatCurrency,
  ChartSkeleton,
  formatDatetime
} from '@blockanalitica/ui';
import { useApi } from '@/api';
import { ChartTimeFilter } from '@/components';
import { getTokenColor } from '@/utils/colors';
import { useMemo, useCallback, useState } from 'react';
import type { TooltipItem, ChartTypeRegistry } from 'chart.js';
import {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';

export interface AssetData {
  [underlying_symbol: string]: {
    period: string;
    amount_usd: string;
  }[];
}

export default function DebtAssetHistoricBarChart({
  marketAddress
}: {
  marketAddress: string;
}) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const [data, error] = useApi<AssetData>({
    url: `/markets/${marketAddress}/debt/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const chartDatasets: ChartDataset[] = useMemo(() => {
    if (!data) return [];
    return Object.entries(data).map(([key, rows]) => ({
      label: key,
      data: rows.map((row) => ({
        x: row.period,
        y: row.amount_usd
      })),
      borderColor: getTokenColor(key) || '#808080',
      backgroundColor: getTokenColor(key) || '#808080'
    }));
  }, [data]);

  const valueFormatter = useCallback((value: string | number) => {
    return (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{ currency: 'USD' }}
      />
    );
  }, []);

  const config: ChartConfig = useMemo(() => {
    return {
      // @ts-ignore
      type: 'bar',
      options: {
        plugins: {
          tooltip: {
            itemSort: (
              a: TooltipItem<keyof ChartTypeRegistry>,
              b: TooltipItem<keyof ChartTypeRegistry>
            ) => {
              const ay = (a.parsed as { y?: number | null }).y ?? 0;
              const by = (b.parsed as { y?: number | null }).y ?? 0;
              return by - ay;
            }
          }
        },

        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true
          }
        },
        elements: {
          bar: {
            hoverBorderWidth: 0
          }
        }
      }
    };
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <Chart
      chartDatasets={chartDatasets}
      config={config}
      valueFormatter={valueFormatter}
      tooltip={{
        valueFormatter: (value) => `${formatCurrency(Number(value))}`,
        titleFormatter: (_value, tootlipItems) =>
          // @ts-ignore
          `${formatDatetime(tootlipItems[0].raw.x, { format: 'MMM dd, yyyy' })}`
      }}
      showKpisAsTooltip
      timeFilter={timeFilter}
    />
  );
}
